/**
 * Option-row ids for `SearchableSelect` / `SearchableMultiSelect` (#13504).
 *
 * Every row id is derived from the popover's own `listboxId`, which is
 * `useId()`-backed and therefore unique per instance. The ids used to be
 * document-global literals — `searchable-select-opt-${idx}`,
 * `searchable-select-clear` and the `searchable-multi-select-*` pair — while
 * the listbox they sat under was already instance-scoped. Two popovers can be
 * open at once (`defaultOpen` exists so an outer trigger can mount a picker
 * already open), and then the ids collided.
 *
 * That collision is not cosmetic. `aria-activedescendant` resolves by
 * `document.getElementById`, which returns the FIRST match, so the second
 * popover's combobox named the FIRST popover's row and a screen reader
 * narrated an option from a list the user was not in. Since #13479 the
 * attribute sits on the search field as part of the combobox wiring, so the
 * id set is part of the named listbox's contract rather than an incidental
 * detail.
 *
 * Three reasons this is a module rather than three template literals inlined
 * at the eight call sites it replaces — four in each component: the
 * scroll-into-view lookup, the `aria-activedescendant` derivation, the option
 * row, and the clear row:
 *
 *  1. **The rows and `aria-activedescendant` must agree exactly.** The
 *     attribute is derived from `focusedIndex` at one place and the rows are
 *     rendered at another; a string that disagrees by one character is an
 *     attribute pointing at nothing, which is silent — no error, no missing
 *     element, just an unannounced option. {@link focusedRowId} is built out
 *     of the same two functions the rows use, so agreement is structural.
 *  2. **Both components need it identically** and they are two files
 *     (#13473, `check:file-size-ratchet`) — the same reason
 *     `./searchable-select-listbox-name`, `./searchable-select-menu-pos` and
 *     `./searchable-select-search-field` exist.
 *  3. **The index space stays flat.** `-opt-${idx}` indexes straight into the
 *     flat `filtered` array, including under grouped mode (#13473), where a
 *     heading is never a member of that array and so cannot shift a row's id.
 *
 * ## Addressing a row from a test or from the component
 *
 * By ATTRIBUTE, never by interpolating the id into a `#id` CSS selector — and
 * the input that makes `#${id}` wrong is the CALLER-SUPPLIED `id` prop, not
 * `useId()`. `listboxId` is `` `${id ?? fallbackId}-listbox` ``, and nothing
 * vets that prop: `id="picker.one"` makes `#picker.one-listbox-opt-0` parse as
 * `#picker` plus `.one-listbox-opt-0` — a perfectly valid selector that
 * matches nothing and throws nothing, so scroll-into-view just stops working.
 * On React 19.3.1 the `useId()` fallback is `_R_0_`, which IS a valid CSS
 * ident and resolves under either form; an earlier version of this comment
 * blamed it anyway, which made the stated reason unfalsifiable and the real
 * hazard invisible.
 *
 * In the components, {@link findRowIn} is the lookup, and it uses no selector
 * at all — see its own comment for why every selector form fails on some legal
 * `id`. In Playwright, where `getElementById` is not available as a locator,
 * specs derive the prefix from the trigger's own `aria-controls` and match on
 * `[id="…"]`, which is what `tests/e2e/journeys/groups/aor-edit-pickers.spec.ts`
 * does: Chromium honours the escapes that jsdom does not, and no fixture id
 * contains a quote. Do not build an `#${id}-…` selector by hand anywhere.
 *
 * The two components no longer have separate id families: a
 * `searchable-select` row and a `searchable-multi-select` row are told apart
 * by their listbox prefix, which is the thing that actually identifies the
 * popover. A spec that used the family name to pick one of two pickers on a
 * page was relying on there being exactly one of each.
 */

/** The `-opt-${idx}` row: `idx` is an offset into the flat filtered array. */
export function optionRowId(listboxId: string, idx: number): string {
  return `${listboxId}-opt-${idx}`
}

/**
 * The clear row — "clear the selection" in `SearchableSelect` (`allowClear`),
 * "Clear all" in `SearchableMultiSelect`. It is index `-1` in both.
 */
export function clearRowId(listboxId: string): string {
  return `${listboxId}-clear`
}

/**
 * The row `aria-activedescendant` should name for a given `focusedIndex`:
 * `null` (no arrow-key focus yet) → `undefined`, `-1` → the clear row,
 * otherwise the option at that offset.
 *
 * Returning `undefined` rather than an empty string matters: React omits the
 * attribute entirely for `undefined`, whereas `aria-activedescendant=""` is
 * a present attribute that resolves to nothing.
 */
export function focusedRowId(
  listboxId: string,
  focusedIndex: number | null,
): string | undefined {
  if (focusedIndex === null) return undefined
  return focusedIndex === -1
    ? clearRowId(listboxId)
    : optionRowId(listboxId, focusedIndex)
}

/**
 * The row `rowId` names, but only if it is inside `listbox` — the lookup the
 * scroll-into-view effect in both components uses.
 *
 * NO CSS SELECTOR IS INVOLVED, and that is the point. A row id carries
 * whatever the caller put in its `id` prop, and every selector form breaks on
 * some legal value of that:
 *
 *  - `#${rowId}` breaks on a `.`, quietly. `id="picker.one"` makes
 *    `#picker.one-listbox-opt-0` parse as `#picker` plus a class — a valid
 *    selector that matches nothing and throws nothing, so the row simply
 *    stops scrolling into view.
 *  - `[id="${rowId}"]` breaks on a `"` or a `\`. Unescaped, a quote closes
 *    the string early and `querySelector` throws a `SyntaxError` from inside
 *    an effect. Escaping those two characters is correct per the CSS grammar
 *    and still does not work here: this repo's jsdom selector engine does not
 *    honour the escapes, so `[id="pick\"er-…"]` returns null in the unit tier
 *    while matching in a browser. Measured, not assumed.
 *  - `CSS.escape` is not available to close that gap — it is undefined in
 *    jsdom, so depending on it would crash every unit test that arrows
 *    through a popover.
 *
 * `getElementById` parses nothing, so it is exact for every possible id. It
 * also resolves the id the same way `aria-activedescendant` does, which is
 * the property this whole module exists to protect — and the containment
 * check is what makes it safe to use despite returning a document-wide first
 * match: if two popovers ever share an id again, this returns null and
 * nothing scrolls, rather than scrolling to another popover's row.
 */
export function findRowIn(
  listbox: HTMLElement,
  rowId: string,
): HTMLElement | null {
  const row = listbox.ownerDocument.getElementById(rowId)
  return row !== null && listbox.contains(row) ? row : null
}
